import React, { useEffect, useRef } from "react";
import Bee from "@beefree.io/sdk";
import { IToken, IBeeConfig } from "@beefree.io/sdk/dist/types/bee";

import { fetchBeeTemplate } from "api/bee-template";

import { fetchBeeToken } from "hooks/api/use-bee-token";

interface BeeProps extends Omit<IBeeConfig, "container"> {
  baseTemplate?: string | object;
  beeConfig?: Omit<IBeeConfig, "container">;
  clientId?: string;
  isEmail?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const BEE_CONTAINER_ID = "bee-sdk-container";
const BASE_TEMPLATE =
  "https://raw.githubusercontent.com/BEE-Plugin/BEE-FREE-templates/master/v2%20Email%20Templates/BF-blank-template.json";

const DEFAULT_CONFIGURATION = {
  container: BEE_CONTAINER_ID,
  language: "en-US",
  autosave: false,
  trackChanges: true,
};

const BeeComponent: React.FC<BeeProps> = ({
  baseTemplate,
  onAutoSave,
  onChange,
  onSend,
  onSaveAsTemplate,
  beeConfig,
  clientId,
  isEmail = true,
  style,
  className,
}) => {
  const beeInstanceRef = useRef<Bee | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    const initializeBee = async () => {
      try {
        const token: IToken = {
          ...(await fetchBeeToken(clientId as string, isEmail ? "email" : "lp")),
          shared: false,
          coediting_session_id: "",
        };

        // Bail out if the component unmounted while we were fetching the token
        if (abortController.signal.aborted) return;

        const beeConfigWithDefaults = {
          ...DEFAULT_CONFIGURATION,
          ...beeConfig,
          onAutoSave,
          onChange,
          onSaveAsTemplate,
          onSend,
          uid: clientId,
          autosave: typeof beeConfig?.autosave === "number" ? beeConfig.autosave : 0,
        };

        const builder = new Bee(token);
        beeInstanceRef.current = builder;

        // Fetch the template if not provided.
        const template =
          baseTemplate ?? (await fetchBeeTemplate(BASE_TEMPLATE, abortController.signal));

        // Bail out again after the (possibly slow) template fetch
        if (abortController.signal.aborted) return;

        builder.start(beeConfigWithDefaults, template as object);
      } catch (error) {
        if ((error as Error)?.name !== "AbortError") {
          console.error("Failed to initialize BEE Plugin:", error);
        }
      }
    };

    initializeBee();

    return () => {
      abortController.abort();
      try {
        beeInstanceRef.current?.bee?.destroy?.();
        beeInstanceRef.current?.instance?.destroy?.();
      } catch {
        // internal destroy may not exist or may throw — safe to ignore
      }
      beeInstanceRef.current = null;
      // Remove the BEE iframe from the DOM so the browser cancels its in-flight
      // connections (token auth, asset loads, WebSocket handshakes). Without this
      // the pending requests from Beefree's servers fill the browser's connection
      // pool and block subsequent API calls on the preview page.
      const container = document.getElementById(BEE_CONTAINER_ID);
      if (container) container.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div id={BEE_CONTAINER_ID} style={style} className={className} />;
};

export default BeeComponent;
